import React, { useState } from 'react'
import { Handle, Position, NodeProps } from 'reactflow'
import { Search, FileText, Database, Globe, Calculator, Settings } from 'lucide-react'

interface ToolNodeData {
  label: string
  config: {
    name: string
    description: string
    parameters: Record<string, any>
  }
}

const ToolNode: React.FC<NodeProps<ToolNodeData>> = ({ data, selected }) => {
  const [showConfig, setShowConfig] = useState(false)
  
  const getToolIcon = (label: string) => {
    if (label.toLowerCase().includes('search')) return Search
    if (label.toLowerCase().includes('file')) return FileText
    if (label.toLowerCase().includes('database')) return Database
    if (label.toLowerCase().includes('api')) return Globe
    if (label.toLowerCase().includes('calculator')) return Calculator
    return Search
  }
  
  const Icon = getToolIcon(data.label)

  return (
    <>
      <div className={`node-base node-tool w-48 h-20 ${selected ? 'ring-2' : ''}`} style={{borderColor: selected ? 'var(--color-node-tool)' : undefined}}>
        <div className="flex items-center h-full p-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3" style={{backgroundColor: 'var(--color-node-tool-bg)'}}>
            <Icon className="w-5 h-5" style={{color: 'var(--color-node-tool)'}} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-900 dark:text-white text-sm truncate">
              {data.label}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {data.config.description}
            </div>
          </div>
          <button
            onClick={() => setShowConfig(true)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <Settings className="w-4 h-4" />
          </button>
        </div>
        
        {/* Input handle */}
        <Handle
          type="target"
          position={Position.Left}
          className="w-3 h-3 bg-white border-2 border-orange-600"
        />
        
        {/* Output handle */}
        <Handle
          type="source"
          position={Position.Right}
          className="w-3 h-3 bg-white border-2 border-orange-600"
        />
      </div>
      
      {/* Configuration Modal */}
      {showConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Configure Tool
              </h3>
              <button
                onClick={() => setShowConfig(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tool Name
                </label>
                <input
                  type="text"
                  value={data.config.name}
                  className="input"
                  placeholder="e.g., Web Search Tool"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={data.config.description}
                  className="input h-20 resize-none"
                  placeholder="What does this tool do?"
                />
              </div>
              
              {/* Tool-specific parameters */}
              {data.label.toLowerCase().includes('search') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Search Engine
                  </label>
                  <select className="input">
                    <option value="google">Google</option>
                    <option value="bing">Bing</option>
                    <option value="duckduckgo">DuckDuckGo</option>
                  </select>
                </div>
              )}
              
              {data.label.toLowerCase().includes('api') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    API Endpoint
                  </label>
                  <input
                    type="url"
                    className="input"
                    placeholder="https://api.example.com/endpoint"
                  />
                </div>
              )}
              
              <div className="flex justify-end space-x-2 pt-4">
                <button
                  onClick={() => setShowConfig(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowConfig(false)}
                  className="btn btn-primary"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ToolNode
